import { Autocomplete, TextField } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';

interface StandardAutocompleteProps {
  optionList: any[];
  handleSelectedOption?(value: any);
  isLoading: boolean;
  defaultValue?: string;
}

export function ReasonAppointmentForm({
  optionList,
  handleSelectedOption,
  isLoading,
  defaultValue,
}: StandardAutocompleteProps) {
  const { i18n } = useLocales();
  const { control, setValue } = useFormContext();

  const reasonCodeWatch = useWatch({
    control,
    name: 'reasonCode',
  });

  const options = useMemo(() => {
    if (!Array.isArray(optionList)) return [];
    return [...optionList].sort((a, b) =>
      a.display?.localeCompare(b.display, undefined, { sensitivity: 'base' }) ?? 0
    );
  }, [optionList]);
  
  const [selectedCode, setSelectedCode] = useState<string>();

  const selectedOption = useMemo(() => {
    if (!selectedCode) {
      return null;
    }

    const foundOption = options.find((item) => item.code === selectedCode);

    if (!foundOption) {
      return null;
    }

    return foundOption;
  }, [options, selectedCode]);

  useEffect(() => {
    if (reasonCodeWatch) {
      setSelectedCode(reasonCodeWatch);
    } else if (defaultValue) {
      setSelectedCode(defaultValue);
      setValue('reasonCode', defaultValue);
    }
  }, [reasonCodeWatch, defaultValue]);

  return (
    <Controller
      name={'reasonCode'}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Autocomplete
          // hidden={false}
          disabled={isLoading}
          value={selectedOption}
          fullWidth
          onChange={(event: React.SyntheticEvent, value: any) => {
            if (handleSelectedOption) {
              handleSelectedOption(value);
            }

            field.onChange(value?.code);
            setSelectedCode(value?.code);
          }}
          options={options}
          getOptionLabel={({ display }) => display ?? ''}
          renderInput={(params) => (
            <TextField
              {...params}
              {...field}
              error={!!error}
              helperText={error?.message}
              label={i18n('patients.details.appointments.formReason', 'crs')}
              variant="outlined"
            />
          )}
        />
      )}
    />
  );
}
